import React, { useState, useEffect } from 'react';
import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Row,
  Section,
  Square,
  Squish,
  Stack,
  SvgPencil,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import {
  EnterpriseSetupTable,
  IEnterpriseSetupTableRowProps,
} from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';
import {
  IGroup,
  useGroupSearch,
  ISearchParams,
} from 'src/utilities/useSearchHooks';
import { Input } from 'src/components/Input/Input';
import { sdk } from 'src/utilities/sdk';
import { pushSuccess, pushError } from 'src/utilities/notifications';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useAsyncTask, AsyncTaskStatus } from 'src/utilities/useAsyncTask';
import { SortFields, SortOrder } from 'src/components/Table/SortableColumn';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

export function EnterpriseGroupsStep(): JSX.Element {
  const { goToPreviousStep, goToNextStep } = useEnterpriseSignupStatus();
  const [hasGroupsAdded, setHasGroupsAdded] = useState(false);
  const { sendTealiumPageView } = useAnalytics();
  const { corpid } = useParams<{ corpid: string }>();

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'create groups',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Section inset="lg">
        <Container variant="md">
          <Stack y="x-sm">
            <Flex justifyContent="space-between">
              <Heading variant="h3">It’s time to create groups</Heading>
              <EnterpriseBackToHomeButton />
            </Flex>
          </Stack>
          <Stack y="sm">
            <Copy variant="body">
              You must create a group to assign meal budgets to employees. You
              can manage your groups at any point from your business admin
              portal.
            </Copy>
          </Stack>
          <EnterpriseSetupTable<IGroup>
            corpId={corpid as string}
            searchPlaceholder="Search by group name"
            noResultsMessage="You don’t have any groups yet"
            searchResource={useGroupSearch}
            hasItemsCb={(hasGroupsAdded) => setHasGroupsAdded(hasGroupsAdded)}
            tableMaxHeight="340px"
            headerColumns={[
              {
                columnName: 'Group name',
                columnWidth: 3,
                sortField: SortFields.NAME,
                sortOrder: SortOrder.ASC,
                isDefaultSort: true,
              },
              { columnName: 'Description', columnWidth: 9 },
            ]}
            rowElementRender={(
              group,
              isEditing,
              toggleEditState,
              updateSearch
            ) => (
              <GroupTableRow
                group={group}
                isEditing={isEditing}
                toggleEditState={toggleEditState}
                updateSearch={updateSearch}
              />
            )}
            addButtonText="Add group"
            footerAddItemRender={(toggleAddState, updateSearch) => (
              <GroupForm
                toggleAddState={toggleAddState}
                updateSearch={updateSearch}
              />
            )}
          />
        </Container>
      </Section>
      <SignupStepFooter containerSize="md">
        <Flex justifyContent="space-between">
          <Button
            variant="tertiary"
            buttonSize="medium"
            onClick={goToPreviousStep}
          >
            Back
          </Button>
          <Flex alignItems="center">
            <Button
              variant="primary"
              buttonSize="medium"
              disabled={!hasGroupsAdded}
              onClick={goToNextStep}
            >
              Save and continue
            </Button>
          </Flex>
        </Flex>
      </SignupStepFooter>
    </>
  );
}

interface IGroupTableRow extends IEnterpriseSetupTableRowProps {
  group: IGroup;
}

interface GroupFormFields {
  groupId: string;
  groupName: string;
  groupDescription: string;
}

interface IGroupForm {
  group?: IGroup;
  isUpdating?: boolean;
  updateSearch: (searchParams?: ISearchParams) => void;
  toggleAddState?: () => void;
  toggleEditState?: (id?: string) => void;
}

function GroupForm({
  group,
  isUpdating,
  toggleEditState,
  toggleAddState,
  updateSearch,
}: IGroupForm): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const { register, handleSubmit, formState, errors } =
    useForm<GroupFormFields>({
      mode: 'onChange',
      defaultValues:
        isUpdating && group
          ? {
              groupId: group.id,
              groupName: group.name,
              groupDescription: group.description,
            }
          : {},
    });

  const { isValid, isSubmitting } = formState;

  async function submitGroup({
    groupId,
    groupName,
    groupDescription,
  }: GroupFormFields): Promise<void> {
    const actionText = isUpdating ? 'updated' : 'added';

    try {
      if (isUpdating) {
        await sdk.http.put(`/corp-admin/${corpid}/groups/${groupId}`, {
          name: groupName,
          description: groupDescription,
        });

        toggleEditState();
      } else {
        await sdk.http.post(`/corp-admin/${corpid}/groups`, {
          name: groupName,
          description: groupDescription,
        });

        toggleAddState();
      }

      pushSuccess(`${groupName} was ${actionText}`);

      updateSearch({ page: 1 });
    } catch (error) {
      pushError(`${groupName} was not ${actionText}. Try again.`);
    }
  }

  return (
    <Flex alignItems="center" as="form">
      <input type="hidden" name="groupId" ref={register} />
      <Stack x="x-sm">
        <Input
          name="groupName"
          id="groupName"
          ariaLabel="Group name"
          placeholder="Group name"
          ref={register({
            required: true,
            maxLength: {
              value: 300,
              message: 'Group name should be 300 characters or less',
            },
          })}
          error={errors.groupName}
        />
      </Stack>
      <Stack x="x-sm" flexGrow={1}>
        <Input
          name="groupDescription"
          id="groupDescription"
          ariaLabel="Group description"
          placeholder="Description (optional)"
          ref={register}
        />
      </Stack>
      <Stack x="x-sm">
        <Button
          variant="primary"
          disabled={!isValid || isSubmitting}
          isLoading={isSubmitting}
          buttonSize="medium"
          onClick={handleSubmit(submitGroup)}
        >
          {isUpdating ? 'Update' : 'Add'}
        </Button>
      </Stack>
      <Button
        type="button"
        variant="tertiary"
        buttonSize="medium"
        disabled={isSubmitting}
        onClick={() => {
          if (isUpdating && toggleEditState) {
            toggleEditState();
          } else if (toggleAddState) {
            toggleAddState();
          }
        }}
      >
        Cancel
      </Button>
    </Flex>
  );
}

function GroupTableRow({
  group,
  isEditing,
  toggleEditState,
  updateSearch,
}: IGroupTableRow): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] =
    useState(false);

  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();

  const isDeleteInProgress = asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS;

  useEffect(() => {
    if (asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      pushSuccess(`${group.name} was removed`);
      setShowRemoveConfirmationModal(false);
      updateSearch({ page: 1 });
    } else if (asyncTaskStatus === AsyncTaskStatus.ERROR) {
      pushError(`${group.name} was not removed. Try again.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.name, asyncTaskStatus]);

  async function deleteGroup(): Promise<void> {
    try {
      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpid}/groups/v2/${group.id}`);
      });
    } catch (error) {
      pushError(`${group.name} was not removed. Try again.`);
    }
  }

  if (isEditing) {
    return (
      <Square inset="sm" data-testid={`group-update-form-${group.id}`}>
        <GroupForm
          group={group}
          isUpdating
          updateSearch={updateSearch}
          toggleEditState={toggleEditState}
        />
      </Square>
    );
  }

  return (
    <>
      <Row gutter={0} data-testid={`group-row-${group.id}`}>
        <Column gutter={0} phone={3}>
          <Squish inset="sm">
            <Copy variant="body">{group.name}</Copy>
          </Squish>
        </Column>
        <Column gutter={0} phone={7}>
          <Squish inset="sm" className="ellipsisOverflow">
            <Copy variant="body">{group.description}</Copy>
          </Squish>
        </Column>
        <Column gutter={0} phone={2}>
          <Flex alignItems="center" justifyContent="flex-end">
            <Stack x="x-sm">
              <Button
                variant="tertiary"
                onClick={() => toggleEditState(group.id)}
              >
                <SvgPencil aria-label="Edit group" />
              </Button>
            </Stack>
            <Button
              variant="tertiary"
              onClick={() => setShowRemoveConfirmationModal(true)}
            >
              <SvgX aria-label="Remove group" />
            </Button>
          </Flex>
        </Column>
      </Row>
      {showRemoveConfirmationModal && (
        <SimpleDialog
          id={group.id}
          title={`Remove "${group.name}"?`}
          description="You’re about to remove this group."
          primaryActionText="Yes, remove"
          isPrimaryButtonLoading={isDeleteInProgress}
          primaryActionFlavor="danger"
          onPrimaryActionClick={deleteGroup}
          disablePrimaryActionButton={isDeleteInProgress}
          secondaryActionText="Cancel"
          secondaryButtonVariant="secondary"
          onSecondaryActionClick={() => setShowRemoveConfirmationModal(false)}
          disableSecondaryActionButton={isDeleteInProgress}
          buttonsDirection="row"
        />
      )}
    </>
  );
}
